import React from 'react';
import { useForm } from 'react-hook-form';
import {
  Box,
  Dialog,
  HStack,
  Portal,
  Stack,
  Text,
  useDisclosure,
  VStack,
} from '@chakra-ui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  useContactSendOtp,
  useCreateContact,
  useTimer,
  useVerifyContact,
} from 'hooks';
import useInputAttributes from 'hooks/useInputAttributes';
import { useTranslation } from 'localization';

import {
  Button,
  DIALOG_ACTION_BUTTON_PROPS,
  Input,
  ServiceDialogBody,
  ServiceDialogCloseTrigger,
  ServiceDialogFooter,
  ServiceDialogHeader,
  serviceDialogShellProps,
} from 'components';
import { countryCode, yup } from 'utils';
import { emailRegex } from 'utils/regex';

const CreateContact: React.FC<{
  buttonTitle: string;
  type: 'EMAIL' | 'MOBILE';
  verifyId?: string;
  onSuccess: () => void;
}> = ({ buttonTitle, type, verifyId = '', onSuccess }) => {
  const { t } = useTranslation('component.settings.profile');
  const [step, setStep] = React.useState<'init' | 'verify'>('init');
  const [selectedCountry, setSelectedCountry] = React.useState<string>('');
  const [directlyToVerify, setDirectlyToVerify] =
    React.useState<boolean>(false);
  const [submittedContactId, setSubmittedContactId] =
    React.useState<string>('');
  const { open, onOpen, onClose } = useDisclosure();

  const { mutateAsync: submitContact, isPending: submitLoading } =
    useCreateContact();
  const { mutateAsync: sendOtp, isPending: sendOtpLoading } =
    useContactSendOtp();
  const { mutateAsync: verifyContact, isPending: verifyLoading } =
    useVerifyContact();

  const { counter, setCounter } = useTimer(0);

  const closeHandler = () => {
    onClose();
    setStep('init');
    setSubmittedContactId('');
    setSelectedCountry('');
    resetSubmit();
    resetVerify();
    onSuccess();
    setDirectlyToVerify(false);
    setCounter(0);
  };

  React.useEffect(() => {
    if (directlyToVerify && verifyId) {
      setStep('verify');
      sendOtp({ id: verifyId })
        .then(() => {
          setCounter(120);
          setSubmittedContactId(verifyId);
        })
        .catch(() => {
          closeHandler();
        });
    }
  }, [directlyToVerify]);

  const formFieldsIdentity =
    type === 'MOBILE'
      ? {
          yup: {
            country: yup.string().label(t('form.country')).required(),
            identity: yup.string().label(t('form.mobile')).required(),
          },
          attributes: {
            country: t('form.country'),
            identity: t('form.mobile'),
          },
        }
      : {
          yup: {
            country: yup.string().label(t('form.country')),
            identity: yup
              .string()
              .email()
              .lowercase()
              .matches(emailRegex, t('email_validate'))
              .label(t('form.email'))
              .required(),
          },
          attributes: {
            country: t('form.country'),
            identity: t('form.email'),
          },
        };

  const {
    register: registerSubmit,
    handleSubmit,
    reset: resetSubmit,
    resetField: resetSubmitField,
    formState: { errors: errorsSubmit },
  } = useForm({
    resolver: yupResolver(yup.object().shape(formFieldsIdentity.yup)),
    defaultValues: {},
  });

  const formPropsSubmit = useInputAttributes(
    registerSubmit,
    errorsSubmit,
    formFieldsIdentity.attributes
  );
  const onSubmit = async (data: { identity: string; country?: string }) => {
    let payload = {
      ...data,
      type: type,
    };
    if (data.country) {
      payload = {
        ...payload,
        identity:
          payload.country + data.identity.replace(payload.country || '', ''),
      };
    }
    submitContact(payload).then((res) => {
      if (res.id) {
        setSubmittedContactId(res.id);
        resetSubmit();
        setStep('verify');
        sendOtp({ id: res.id }).then(() => {
          setCounter(120);
        });
      }
    });
  };

  const formFieldsVerify = {
    yup: {
      code: yup.string().label(t('form.code')).required(),
    },
    attributes: {
      code: t('form.code'),
    },
  };

  const {
    register: registerVerify,
    handleSubmit: handleVerify,
    reset: resetVerify,

    formState: { errors: errorsVerify },
  } = useForm({
    resolver: yupResolver(yup.object().shape(formFieldsVerify.yup)),
    defaultValues: {},
  });

  const formPropsVerify = useInputAttributes(
    registerVerify,
    errorsVerify,
    formFieldsVerify.attributes
  );
  const onVerify = async (data: { code: string }) => {
    verifyContact({ data, id: submittedContactId }).then(() => {
      resetVerify();
      closeHandler();
    });
  };

  const setOtpHandler = () => {
    sendOtp({ id: submittedContactId }).then(() => {
      setCounter(120);
    });
  };

  const selectedFlag = countryCode.find(
    (item) => item.value === selectedCountry
  )?.flagSvg;

  return (
    <Box>
      <Button
        variant="ghost"
        title={buttonTitle}
        fontSize={verifyId ? 'xs' : 'md'}
        onClick={() => {
          if (verifyId) {
            setDirectlyToVerify(true);
          }
          onOpen();
        }}
      />
      <Dialog.Root
        size="md"
        placement="center"
        closeOnInteractOutside={false}
        open={open}
        onOpenChange={(e: { open: boolean }) => {
          if (!e.open) {
            closeHandler();
          }
        }}
      >
        <Portal>
          <Dialog.Backdrop />
          <Dialog.Positioner>
            <Dialog.Content {...serviceDialogShellProps()}>
              <ServiceDialogHeader>
                {type === 'MOBILE'
                  ? t('dialogTitleMobile')
                  : t('dialogTitleEmail')}
              </ServiceDialogHeader>
              <ServiceDialogCloseTrigger />
              {step === 'init' ? (
                <form onSubmit={handleSubmit(onSubmit)}>
                  <ServiceDialogBody>
                    <Stack gap={3} w="100%">
                      <Text fontSize="sm" color="body2" lineHeight="tall">
                        {type === 'MOBILE'
                          ? t('dialogInitHintMobile')
                          : t('dialogInitHintEmail')}
                      </Text>
                      <VStack w="100%" gap={3}>
                        {type === 'MOBILE' && (
                          <Box w="100%">
                            <Input
                              {...formPropsSubmit.country}
                              isSelectBox
                              options={countryCode}
                              onChange={(
                                e: React.ChangeEvent<HTMLSelectElement>
                              ) => {
                                setSelectedCountry(e.target.value);
                                resetSubmitField('identity');
                              }}
                              leftValue={
                                selectedFlag ? (
                                  <img
                                    src={selectedFlag}
                                    alt="-"
                                    width="20px"
                                    height="20px"
                                  />
                                ) : (
                                  ''
                                )
                              }
                            />
                          </Box>
                        )}
                        <Box w="100%">
                          <Input
                            {...formPropsSubmit.identity}
                            leftValue={selectedCountry}
                          />
                        </Box>
                      </VStack>
                    </Stack>
                  </ServiceDialogBody>
                  <ServiceDialogFooter>
                    <HStack justifyContent="flex-end" gap={2} flexWrap="wrap">
                      <Button
                        variant="ghost"
                        title={t('cancel')}
                        onClick={() => closeHandler()}
                        {...DIALOG_ACTION_BUTTON_PROPS}
                      />
                      <Button
                        variant="filled"
                        title={t('dialogSubmit')}
                        type="submit"
                        isLoading={submitLoading}
                        {...DIALOG_ACTION_BUTTON_PROPS}
                      />
                    </HStack>
                  </ServiceDialogFooter>
                </form>
              ) : (
                <form onSubmit={handleVerify(onVerify)}>
                  <ServiceDialogBody>
                    <Stack gap={3} w="100%">
                      <Text fontSize="sm" color="body2" lineHeight="tall">
                        {t('dialogVerifyHint')}
                      </Text>
                      <Box w="100%">
                        <Input
                          {...formPropsVerify.code}
                          textAlign="center"
                          placeholder="     -     -     -     -     -     -     "
                        />
                      </Box>
                      <Button
                        variant="outline"
                        w="100%"
                        title={
                          counter === 0
                            ? t('dialogSendCode')
                            : counter.toString()
                        }
                        onClick={() => setOtpHandler()}
                        isLoading={sendOtpLoading}
                        isDisabled={counter !== 0}
                        type="button"
                        {...DIALOG_ACTION_BUTTON_PROPS}
                      />
                    </Stack>
                  </ServiceDialogBody>
                  <ServiceDialogFooter>
                    <HStack justifyContent="flex-end" gap={2} flexWrap="wrap">
                      <Button
                        variant="ghost"
                        title={t('cancel')}
                        onClick={() => closeHandler()}
                        {...DIALOG_ACTION_BUTTON_PROPS}
                      />
                      <Button
                        variant="filled"
                        title={t('dialogSubmit')}
                        type="submit"
                        isLoading={verifyLoading}
                        {...DIALOG_ACTION_BUTTON_PROPS}
                      />
                    </HStack>
                  </ServiceDialogFooter>
                </form>
              )}
            </Dialog.Content>
          </Dialog.Positioner>
        </Portal>
      </Dialog.Root>
    </Box>
  );
};

export default CreateContact;
